import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Calendar, ChevronLeft, ChevronRight, CheckCircle2, HelpCircle } from 'lucide-react';
import { Tooltip as UITooltip, TooltipContent as UITooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface DividendEvent {
  symbol: string;
  date: string;
  recordDate: string | null;
  paymentDate: string | null;
  declarationDate: string | null;
  dividend: number;
  yield: number | null;
  confirmed: boolean;
}

interface DividendCalendarProps {
  tickers: string[];
  quantities?: Record<string, number>;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DividendCalendar = ({ tickers, quantities = {} }: DividendCalendarProps) => {
  const [dividends, setDividends] = useState<DividendEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dataSource, setDataSource] = useState<string | null>(null);

  useEffect(() => {
    if (tickers.length > 0) fetchDividends();
  }, [tickers]);

  const fetchDividends = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('dividend-calendar', {
        body: { tickers },
      });
      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || 'Failed to fetch dividend calendar');
      setDividends(data.dividends || []);
      setDataSource(data.source || null);
    } catch (e: any) {
      setError(e.message);
      setDividends([]);
    } finally {
      setLoading(false);
    }
  };

  // Group dividends by month for the selected year
  const monthlyEstimates = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => ({
      month: MONTHS[i],
      monthNum: i,
      confirmed: 0,
      estimated: 0,
      events: [] as (DividendEvent & { estIncome: number })[],
    }));

    dividends.forEach(d => {
      const payDate = d.paymentDate || d.date;
      if (!payDate) return;
      const dt = new Date(payDate);
      if (dt.getFullYear() !== selectedYear) return;
      const monthIdx = dt.getMonth();
      const qty = quantities[d.symbol] || quantities[d.symbol?.toUpperCase()] || 1;
      const estIncome = d.dividend * qty;

      if (d.confirmed) {
        months[monthIdx].confirmed += estIncome;
      } else {
        months[monthIdx].estimated += estIncome;
      }
      months[monthIdx].events.push({ ...d, estIncome });
    });

    return months;
  }, [dividends, selectedYear, quantities]);

  const totalConfirmed = monthlyEstimates.reduce((s, m) => s + m.confirmed, 0);
  const totalEstimated = monthlyEstimates.reduce((s, m) => s + m.estimated, 0);
  const grandTotal = totalConfirmed + totalEstimated;

  const chartData = monthlyEstimates.map(m => ({
    month: m.month,
    confirmed: Math.round(m.confirmed * 100) / 100,
    estimated: Math.round(m.estimated * 100) / 100,
    total: Math.round((m.confirmed + m.estimated) * 100) / 100,
  }));

  // Upcoming dividends (next 60 days)
  const upcoming = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() + 60);
    return dividends
      .filter(d => {
        const dt = new Date(d.paymentDate || d.date);
        return dt >= now && dt <= cutoff;
      })
      .sort((a, b) => (a.paymentDate || a.date).localeCompare(b.paymentDate || b.date))
      .slice(0, 20);
  }, [dividends]);

  if (tickers.length === 0) return null;

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-500" />
              Dividend Calendar & Estimates
              {dataSource && (
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <Badge variant={dataSource === 'fmp' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 cursor-help">
                        {dataSource === 'fmp' ? 'FMP' : dataSource === 'nasdaq' ? 'Nasdaq' : dataSource.toUpperCase()}
                      </Badge>
                    </TooltipTrigger>
                    <UITooltipContent side="bottom" className="max-w-[260px] text-xs">
                      {dataSource === 'nasdaq'
                        ? 'Using Nasdaq API as fallback — FMP returned a rate-limit or payment error.'
                        : 'Using Financial Modeling Prep as the primary data source. Falls back to Nasdaq if unavailable.'}
                    </UITooltipContent>
                  </UITooltip>
                </TooltipProvider>
              )}
            </CardTitle>
            <CardDescription>Upcoming dividend payments and monthly income projections</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedYear(y => y - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold w-12 text-center">{selectedYear}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedYear(y => y + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading dividend calendar...</span>
          </div>
        ) : error ? (
          <p className="text-sm text-destructive text-center py-8">{error}</p>
        ) : dividends.length === 0 ? null : (
          <div className="space-y-6">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Confirmed: ${totalConfirmed.toFixed(2)}
              </Badge>
              <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                <HelpCircle className="h-3 w-3 mr-1" />
                Estimated: ${totalEstimated.toFixed(2)}
              </Badge>
              <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30">
                Total {selectedYear}: ${grandTotal.toFixed(2)}
              </Badge>
            </div>

            {/* Monthly Stacked Bar Chart */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Monthly Dividend Income — {selectedYear}</p>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" tickFormatter={v => `$${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: any, name: string) => [`$${Number(value).toFixed(2)}`, name === 'confirmed' ? 'Confirmed' : 'Estimated']}
                  />
                  <Bar dataKey="confirmed" stackId="div" fill="hsl(142 76% 36%)" radius={[0, 0, 0, 0]} name="confirmed" />
                  <Bar dataKey="estimated" stackId="div" fill="hsl(47 96% 53% / 0.6)" radius={[4, 4, 0, 0]} name="estimated" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Grid */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Month-by-Month Breakdown</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {monthlyEstimates.map((m, i) => {
                  const total = m.confirmed + m.estimated;
                  const hasEvents = m.events.length > 0;
                  return (
                    <div
                      key={i}
                      className={`border rounded-lg p-2.5 text-center transition-colors ${
                        hasEvents ? 'bg-muted/40 border-border' : 'bg-muted/10 border-border/50'
                      }`}
                    >
                      <p className="text-xs font-semibold text-muted-foreground">{m.month}</p>
                      <p className={`text-sm font-bold mt-0.5 ${total > 0 ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                        ${total.toFixed(0)}
                      </p>
                      {m.events.length > 0 && (
                        <div className="flex justify-center gap-0.5 mt-1">
                          {m.events.some(e => e.confirmed) && (
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Confirmed" />
                          )}
                          {m.events.some(e => !e.confirmed) && (
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Estimated" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming Dividends Table */}
            {upcoming.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Upcoming Dividends (Next 60 Days)</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs">Ticker</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs">Ex-Date</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground text-xs">Pay Date</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground text-xs">$/Share</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground text-xs">Est. Income</th>
                        <th className="text-center py-2 px-3 font-medium text-muted-foreground text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.map((d, i) => {
                        const qty = quantities[d.symbol] || quantities[d.symbol?.toUpperCase()] || 1;
                        const estIncome = d.dividend * qty;
                        return (
                          <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                            <td className="py-2 px-3 font-medium text-foreground">{d.symbol}</td>
                            <td className="py-2 px-3 text-muted-foreground">{d.date || '—'}</td>
                            <td className="py-2 px-3 text-muted-foreground">{d.paymentDate || '—'}</td>
                            <td className="py-2 px-3 text-right font-mono text-foreground">${d.dividend.toFixed(4)}</td>
                            <td className="py-2 px-3 text-right font-mono font-semibold text-foreground">${estIncome.toFixed(2)}</td>
                            <td className="py-2 px-3 text-center">
                              {d.confirmed ? (
                                <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                                  Confirmed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
                                  Estimated
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DividendCalendar;
